/**
 * What a player sees on their OWN phone after scanning the host's table QR (src/ui/tableClient.ts). No activation code and no
 * location check — the venue check happens on the host's phone — so there are only two ways in:
 *  1. "I already have a User ID": nickname + PIN.
 *  2. "I'm new": choose a nickname and a PIN (a request the staff approve, unless approval is switched off).
 * Either way the player agrees once a day (the box is not shown again until the activation code changes, see
 * `guestConsentGiven` in tableClient.ts), and the notice says only what is true for a guest: nothing here reads their phone's
 * location.
 */

import { renderBrandHeader } from '../branding';
import { esc } from '../helpers';
import type { TableMember } from '../tableClient';
import { termsOfUseHtml } from './activation';

export type JoinMode = 'existing' | 'new';

/** What the guest agrees to (approved by Indra 2026-09-24). `tests/ui/tableJoin.test.ts` ties it to the code. */
export function guestNoticeHtml(): string {
  return `
    <p><strong>Joining the leaderboard</strong><br />
    To take part, we keep your chosen nickname/User ID; your PIN, stored only in scrambled (hashed) form so venue staff cannot
    view or retrieve it; your game results and details, including points, rounds and wins; and the other players at your table.
    Your data is stored with our hosting provider. We use it to operate the leaderboard, calculate rankings and scores, verify
    game results, and detect or investigate duplicate, manipulated, fraudulent or otherwise suspicious game submissions. Your
    phone's location is not checked when you join; the venue check is done on the host's device.</p>
    <p>We do not collect your name, email address or phone number. Your nickname/User ID and your score or ranking may be
    visible to other players. The venue may also show the leaderboard — nicknames, scores and rankings — on a TV screen at the
    venue. Game details and results are deleted 90 days after the leaderboard period ends, and an account is
    deleted after 90 days without playing. You may ask venue staff to delete your account and data at any time. Taking part is
    voluntary.</p>
  `;
}

/** The label beside the guest's consent box. */
export const GUEST_CONSENT_TEXT = 'I agree to this and to the full Terms of Use.';

export interface TableJoinActions {
  signIn(params: { nickname: string; pin: string; agreed: boolean }): void;
  register(params: { nickname: string; pin: string; pin2: string; agreed: boolean }): void;
}

export function renderTableJoin(
  root: HTMLElement,
  opts: { mode?: JoinMode; nickname?: string; error?: string; notice?: string; /** They already agreed today: no box, the notice stays readable. */ consentGiven?: boolean },
  actions: TableJoinActions,
): void {
  const mode: JoinMode = opts.mode === 'new' ? 'new' : 'existing';
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Join the table</h1>
      <p class="muted">The host has opened a leaderboard table. Sign in with your own nickname and PIN — nobody else needs to type them.</p>
      ${opts.error ? `<div class="error-box">${esc(opts.error)}</div>` : ''}
      ${opts.notice ? `<div class="lb-note">${esc(opts.notice)}</div>` : ''}

      <div class="seating-choice" role="radiogroup" aria-label="Do you have a User ID?">
        <label class="seating-option">
          <input type="radio" name="joinMode" value="existing" ${mode === 'existing' ? 'checked' : ''} />
          <span><strong>I already have a User ID</strong><small>Enter your nickname and PIN.</small></span>
        </label>
        <label class="seating-option">
          <input type="radio" name="joinMode" value="new" ${mode === 'new' ? 'checked' : ''} />
          <span><strong>I'm new — create a User ID</strong><small>Choose a nickname and a 4-digit PIN only you know.</small></span>
        </label>
      </div>

      <label for="joinNick">Nickname</label>
      <input type="text" id="joinNick" autocomplete="off" maxlength="20" value="${esc(opts.nickname ?? '')}" />
      <label for="joinPin">PIN (4 digits)</label>
      <input type="password" id="joinPin" inputmode="numeric" maxlength="4" autocomplete="off" />
      <div id="joinNewOnly" style="display:${mode === 'new' ? '' : 'none'}">
        <label for="joinPin2">PIN again</label>
        <input type="password" id="joinPin2" inputmode="numeric" maxlength="4" autocomplete="off" />
        <p class="muted">Your User ID works straight away: the staff approve each table, not each player.</p>
      </div>
      <p class="muted" id="joinUnlockHint" style="display:${mode === 'existing' ? '' : 'none'};margin:0.5rem 0 0">
        Staff just unlocked your account? Your PIN is now the standard temporary one — you will be asked to choose your own straight away.
      </p>

      ${
        opts.consentGiven
          ? `<details id="joinNotice" style="margin-top:1rem"><summary>You agreed to this earlier today — read it again</summary>${guestNoticeHtml()}</details>`
          : `<div id="joinNotice" style="margin-top:1rem">${guestNoticeHtml()}</div>
      <label class="checkbox-row" style="display:flex;gap:0.5rem;align-items:flex-start">
        <input type="checkbox" id="joinAgree" style="margin-top:0.25rem" />
        <span>${esc(GUEST_CONSENT_TEXT)}</span>
      </label>`
      }
      <details style="margin-top:0.5rem"><summary>Read the full Terms of Use</summary>${termsOfUseHtml()}</details>

      <button id="joinBtn" style="margin-top:1rem;width:100%">${mode === 'new' ? 'Create User ID and ask to join' : 'Join table'}</button>
    </div>
  `;

  const value = (id: string) => root.querySelector<HTMLInputElement>(`#${id}`)?.value.trim() ?? '';
  const chosen = (): JoinMode => (root.querySelector<HTMLInputElement>('input[name="joinMode"]:checked')?.value === 'new' ? 'new' : 'existing');
  const btn = root.querySelector<HTMLButtonElement>('#joinBtn')!;

  for (const radio of root.querySelectorAll<HTMLInputElement>('input[name="joinMode"]')) {
    radio.addEventListener('change', () => {
      const isNew = chosen() === 'new';
      root.querySelector<HTMLElement>('#joinNewOnly')!.style.display = isNew ? '' : 'none';
      root.querySelector<HTMLElement>('#joinUnlockHint')!.style.display = isNew ? 'none' : '';
      btn.textContent = isNew ? 'Create User ID and ask to join' : 'Join table';
    });
  }

  btn.addEventListener('click', () => {
    btn.disabled = true;
    const agreed = opts.consentGiven === true || root.querySelector<HTMLInputElement>('#joinAgree')!.checked;
    if (chosen() === 'new') actions.register({ nickname: value('joinNick'), pin: value('joinPin'), pin2: value('joinPin2'), agreed });
    else actions.signIn({ nickname: value('joinNick'), pin: value('joinPin'), agreed });
  });
}

/** Signed in: waiting for the other players and for the host to start. `update` keeps the list current without a redraw. */
export function renderTableWaiting(
  root: HTMLElement,
  opts: { nickname: string; members: TableMember[]; notice?: string },
): { update(members: TableMember[]): void } {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card" data-table-screen="open">
      <h1>You're in, ${esc(opts.nickname)}</h1>
      <p class="muted">Stay on this screen. The host will seat everyone and start the game.</p>
      ${opts.notice ? `<div class="lb-note">${esc(opts.notice)}</div>` : ''}
      <h2>At this table</h2>
      <div id="waitMembers"></div>
    </div>
  `;
  const box = root.querySelector<HTMLElement>('#waitMembers')!;
  const draw = (members: TableMember[]) => {
    box.innerHTML = `<ul class="stack" style="list-style:none;padding:0;margin:0">${members
      .map((m) => `<li>${esc(m.nickname)}${m.isHost ? ' <span class="muted">(host)</span>' : ''}</li>`)
      .join('')}</ul>`;
  };
  draw(opts.members);
  return { update: draw };
}

/** A plain message on the guest phone: closed table, started game, removed by the host, no connection. */
export function renderTableMessage(
  root: HTMLElement,
  title: string,
  message: string,
  action?: { label: string; run(): void },
): void {
  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>${esc(title)}</h1>
      <p class="muted">${esc(message)}</p>
      ${action ? `<button type="button" id="tblMsgBtn" style="margin-top:1rem;width:100%">${esc(action.label)}</button>` : ''}
    </div>
  `;
  root.querySelector<HTMLButtonElement>('#tblMsgBtn')?.addEventListener('click', () => action!.run());
}
